import URI from 'jsuri';
import Q from 'q';
import _ from 'underscore';

import path from 'js/lib/path';
import stringKeyTuple from 'js/lib/stringKeyTuple';
import user from 'js/lib/user';

import api from 'pages/open-course/common/naptimeApi';
import { memoizedCourseGradePromise } from 'pages/open-course/common/promises/courseGrade';
import { memoizedCourseViewGradePromise } from 'pages/open-course/common/promises/courseViewGrade';

import { memoizedCourseProgressPromise } from 'bundles/in-course-stores/promises/fullCourseProgress';

type CreateResponse = Q.Promise<{ body: $TSFixMe; createdId: never }>;

type Args = Record<string, string | number | string[]>;

function makeUri(resource: string, args?: Args | null) {
  const uri = new URI(resource);

  if (args) {
    _(args).each(function (value, key) {
      // jsuri DOES NOT support arrays as param values, however before better typing this was being done and is expected
      // so just allow whatever undocumented behavior jsuri has when passing an array...
      uri.addQueryParam(key, value as any);
    });
  }

  return uri;
}

class NaptimeItemClient {
  declare itemId: string;

  declare courseId: string;

  declare courseSlug: string;

  constructor({ itemId, courseId, courseSlug }: { itemId: string; courseId: string; courseSlug: string }) {
    this.itemId = itemId;
    this.courseId = courseId;
    this.courseSlug = courseSlug;
  }

  /**
   * Call a naptime create(). Automatically refreshes any course progress affected by the create().
   *
   * @param {string} resource The name of the naptime resource.
   * @param {object} body The body of the create request.
   * @param {object} args The URI arguments.
   * @param {boolean} mightUpdateProgress whether calling this might update any course progress. Defaults to true.
   * @returns Promise for a {body: object, createdId: string}.
   */
  create(resource: string, body: unknown, args?: Args | null, mightUpdateProgress?: boolean): CreateResponse {
    const uri = makeUri(resource, args);

    if (mightUpdateProgress === undefined) {
      mightUpdateProgress = true;
    }

    const requestOptions = {
      data: body,
    };

    return Q.all([
      memoizedCourseGradePromise(this.courseId),
      // @ts-expect-error ts-migrate(2554) FIXME: Expected 2 arguments, but got 1.
      memoizedCourseViewGradePromise(user.get().id + '~' + this.courseId),
      memoizedCourseProgressPromise(this.courseSlug),
      api.post(uri.toString(), requestOptions).then((body) => ({ body })),
    ]).spread(function (courseGrade, courseViewGrade, courseProgress, apiResponse) {
      if (mightUpdateProgress) {
        courseGrade.refresh();
        courseViewGrade.refresh();
        courseProgress.trigger('refresh');
      }
      return apiResponse;
    });
  }

  /**
   * Call a naptime get().
   *
   * @param {string} resource The name of the naptime resource.
   * @param {string} id The id of the requested object.
   * @param {object} args The URI arguments.
   * @param {boolean} mightRevealChangedProgress Whether calling this might have an affect on course progress.
   * @returns Promise for the request.
   */
  get(resource: string, id: string = '', args?: Args, mightRevealChangedProgress?: boolean) {
    const uri = makeUri(path.join(resource, id), args);

    return Q.all([api.get(uri.toString()), memoizedCourseProgressPromise(this.courseSlug)]).spread(
      function (response, courseProgress) {
        if (mightRevealChangedProgress) {
          courseProgress.trigger('refresh');
        }

        return response;
      }
    );
  }

  /**
   * Call a naptime get() on a resource whose ids are the [[CourseItemId]]s for this client's `itemMetadata`.
   *
   * @param {string} resource The name of the naptime resource.
   * @param {object} args The URI arguments.
   * @param {boolean} mightRevealChangedProgress Whether calling this might have an affect on course progress.
   * @returns Promise for the request.
   */
  getWithCourseItemId(resource: string, args?: Args, mightRevealChangedProgress?: boolean) {
    const id = stringKeyTuple.tupleToStringKey([this.courseId, this.itemId]);

    return this.get(resource, id, args, mightRevealChangedProgress);
  }

  /**
   * Call a naptime get() on a resource whose ids are the [[UserCourseItemId]]s for this client's `itemMetadata` and for
   * the current logged in user.
   *
   * @param {string} resource The name of the naptime resource.
   * @param {object} args The URI arguments.
   * @param {boolean} mightRevealChangedProgress Whether calling this might have an affect on course progress.
   * @returns Promise for the request.
   */
  getWithUserCourseItemId(resource: string, args: Args, mightRevealChangedProgress?: boolean) {
    const userId = user.get().id;
    const id = stringKeyTuple.tupleToStringKey([userId.toString(), this.courseId, this.itemId]);

    return this.get(resource, id, args, mightRevealChangedProgress);
  }

  /**
   * Call a naptime finder.
   *
   * @param {string} resource The name of the naptime resource.
   * @param {string} finderName The name of the finder for the naptime resource.
   * @param {object} args The URI arguments.
   * @param {boolean} mightRevealChangedProgress Whether calling this might have an affect on course progress.
   * @returns Promise for the request.
   */
  finder(resource: string, finderName: string, args: Args, mightRevealChangedProgress?: boolean) {
    const uri = makeUri(resource, _({ q: finderName }).extend(args || {}));

    return Q.all([api.get(uri.toString()), memoizedCourseProgressPromise(this.courseSlug)]).spread(
      function (response, courseProgress) {
        if (mightRevealChangedProgress) {
          courseProgress.trigger('refresh');
        }

        return response;
      }
    );
  }

  /**
   * Call a naptime action.
   *
   * @param {string} resource The name of the naptime resource.
   * @param {string} action The name of the action for the naptime resource.
   * @param {object} args The URI arguments.
   * @returns Promise for the request.
   */
  action(resource: string, action: string, args: Args) {
    const uri = makeUri(resource, _({ action }).extend(args || {}));

    return api.post(uri.toString());
  }
}

export default NaptimeItemClient;
